import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, Phone } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => { document.title = "Privacy Policy | Cheap4Teach"; }, []);

  const Section = ({ title, children }) => (
    <div className="mb-8">
      <h2 className="text-xl font-['Fredoka'] font-bold mb-3 text-[#FF1F87]">{title}</h2>
      <div className="text-black/80 space-y-2 leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="flex items-center gap-4 mb-10">
        <span className="p-3 rounded-2xl border-[3px] border-black bg-[#FF1F87]/10">
          <Shield className="w-8 h-8 text-[#FF1F87]" />
        </span>
        <div>
          <h1 className="text-3xl font-['Fredoka'] font-bold">Privacy Policy</h1>
          <p className="text-sm text-black/50">Last updated: May 2026</p>
        </div>
      </div>

      <div className="c4t-card p-8">
        <p className="mb-8 text-black/80 leading-relaxed">
          At Cheap4Teach, we take your privacy seriously. This policy explains what personal
          information we collect, why we collect it, and how we use it. We operate in accordance
          with Jamaica's <strong>Data Protection Act (2023)</strong>.
        </p>

        <Section title="1. Who We Are">
          <p>
            Cheap4Teach is an online shop selling classroom supplies and educational resources
            for Caribbean teachers. We are based in Jamaica and can be reached at:
          </p>
          <div className="mt-3 flex flex-col gap-1">
            <span className="flex items-center gap-2 font-semibold"><Mail className="w-4 h-4 text-[#FF1F87]" /> cheap4teach@gmail.com</span>
            <span className="flex items-center gap-2 font-semibold"><Phone className="w-4 h-4 text-[#FF1F87]" /> WhatsApp: 876 319 1763</span>
          </div>
        </Section>

        <Section title="2. What We Collect">
          <p>We collect the following information when you use our site:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li><strong>Account details</strong> — your name and email address when you sign up or log in with Google</li>
            <li><strong>Order details</strong> — your name, email, phone number, shipping address, and items purchased</li>
            <li><strong>Payment proof</strong> — if you pay by bank transfer, you may upload a screenshot of your receipt</li>
            <li><strong>Usage data</strong> — pages visited and actions taken on the site, collected via PostHog analytics (including session recordings)</li>
          </ul>
          <p className="mt-2">We do <strong>not</strong> store credit card numbers or bank account details.</p>
        </Section>

        <Section title="3. Why We Collect It">
          <ul className="list-disc ml-5 space-y-1">
            <li>To process and fulfil your orders</li>
            <li>To send you order confirmation and status update emails</li>
            <li>To manage your account and purchase history</li>
            <li>To improve the website experience (via analytics)</li>
            <li>To send promotional notifications, only if you opt in</li>
          </ul>
        </Section>

        <Section title="4. How Long We Keep It">
          <p>
            Order records are kept for up to <strong>3 years</strong> for accounting and
            customer service purposes. Account data is retained until you request deletion.
            Analytics data is retained per PostHog's standard retention policy.
          </p>
        </Section>

        <Section title="5. Who We Share It With">
          <p>We share limited data with the following third parties to operate our store:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li><strong>Resend</strong> — to send transactional emails (order confirmations, status updates)</li>
            <li><strong>WiPay</strong> — to process card payments securely</li>
            <li><strong>Google</strong> — for sign-in functionality (Google OAuth)</li>
            <li><strong>PostHog</strong> — for site analytics and session recordings</li>
            <li><strong>Cloudflare R2</strong> — to store digital product files for download</li>
          </ul>
          <p className="mt-2">We do not sell your personal data to anyone.</p>
        </Section>

        <Section title="6. Your Rights">
          <p>Under the Jamaica Data Protection Act, you have the right to:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>Know what personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Opt out of promotional communications at any time</li>
          </ul>
          <p className="mt-2">
            To exercise any of these rights, email us at{" "}
            <a href="mailto:cheap4teach@gmail.com" className="text-[#FF1F87] font-semibold underline">
              cheap4teach@gmail.com
            </a>.
          </p>
        </Section>

        <Section title="7. Cookies & Tracking">
          <p>
            We use <strong>PostHog</strong> to understand how visitors use our site.
            This includes page views, clicks, and session recordings. No cookie consent
            is required for strictly necessary functionality, but our analytics tools do
            set tracking cookies. By continuing to use the site you agree to this.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            We protect your data using industry-standard security measures including
            encrypted connections (HTTPS), hashed passwords, and access-controlled
            admin systems. No system is 100% secure, but we take every reasonable
            precaution to protect your information.
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            We may update this policy occasionally. Any changes will be posted on this
            page with an updated date. Continued use of the site after changes means
            you accept the updated policy.
          </p>
        </Section>

        <div className="mt-8 pt-6 border-t border-black/10 text-center">
          <p className="text-sm text-black/50 mb-4">Questions about your privacy? We're happy to help.</p>
          <Link to="/contact" className="c4t-button">Contact Us</Link>
        </div>
      </div>
    </div>
  );
}
